/**
 * Version Check Component
 *
 * Integrates version checking with update dialog
 */

import { memo, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { UpdateDialog } from '@/components/UpdateDialog';
import { VersionDebugInfo } from '@/components/VersionDebugInfo';
import { useVersionCheck } from '@/hooks/useVersionCheck';

interface VersionCheckProps {
  /**
   * Polling interval in milliseconds (default: 60000ms = 1 minute)
   */
  pollingInterval?: number;

  /**
   * Whether to enable WebSocket updates (default: true)
   */
  enableWebSocket?: boolean;

  /**
   * WebSocket URL (optional)
   */
  wsUrl?: string;
}

export const VersionCheck = memo<VersionCheckProps>(
  ({ pollingInterval = 120000, enableWebSocket = false, wsUrl }) => {
    const [isUpdating, setIsUpdating] = useState(false);

    const {
      hasUpdate,
      latestVersion,
      isForced,
      updateApp,
      dismissUpdate,
      markAsSeen,
      checkVersion,
      isChecking,
      error,
      currentVersion,
    } = useVersionCheck({
      pollingInterval,
      enableWebSocket,
      wsUrl,
      checkOnMount: false, // Disabled - manual check only
      enablePolling: false, // Disabled - no automatic polling
    });

    // Debug logging
    useEffect(() => {
    }, [hasUpdate, latestVersion, currentVersion, isForced, isChecking, error]);

    const handleUpdate = async () => {
      setIsUpdating(true);

      try {
        // Mark as seen before updating
        await markAsSeen();

        // Show update toast
        toast.info('جاري تحديث التطبيق...', {
          description: 'سيتم إعادة تحميل الصفحة خلال لحظات',
        });

        // Small delay to show the toast
        setTimeout(() => {
          updateApp();
        }, 1000);
      } catch (error) {
        console.error('❌ [VersionCheck] Update failed:', error);
        setIsUpdating(false);

        toast.error('فشل في التحديث', {
          description: 'حدث خطأ أثناء تحديث التطبيق. يرجى المحاولة مرة أخرى.',
        });
      }
    };

    const handleLater = () => {
      dismissUpdate();

      toast.info('تم تأجيل التحديث', {
        description: 'سيتم عرض التحديث مرة أخرى في المرة القادمة',
      });
    };

    return (
      <>
        {/* Debug Info - Only shows in development */}
        <VersionDebugInfo
          onCheckVersion={checkVersion}
          hasUpdate={hasUpdate}
          latestVersion={latestVersion?.version}
          isChecking={isChecking}
          error={error}
        />

        {/* Update Dialog - Only shows when update is available */}
        {hasUpdate && latestVersion && (
          <UpdateDialog
            open={hasUpdate}
            version={latestVersion.version}
            isForced={isForced}
            changelog={latestVersion.changelog}
            onUpdate={handleUpdate}
            onLater={isForced ? undefined : handleLater}
            isUpdating={isUpdating}
          />
        )}
      </>
    );
  },
);

VersionCheck.displayName = 'VersionCheck';
